import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const ISLAND_LABELS = [
  { name: 'Isabela', coords: [-0.55, -91.15] },
  { name: 'Fernandina', coords: [-0.36, -91.55] },
  { name: 'Santiago', coords: [-0.24, -90.76] },
  { name: 'Santa Cruz', coords: [-0.62, -90.31] },
  { name: 'San Cristóbal', coords: [-0.87, -89.48] },
  { name: 'Floreana', coords: [-1.3, -90.44] },
  { name: 'Española', coords: [-1.38, -89.68] },
  { name: 'Genovesa', coords: [0.31, -89.96] },
];

function experienceIcon(index, active = false) {
  return L.divIcon({
    className: 'galapagos-marker-shell',
    html: `<span class="galapagos-marker${active ? ' is-active' : ''}">${index + 1}</span>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });
}

function islandLabelIcon(name) {
  return L.divIcon({
    className: 'galapagos-island-label-shell',
    html: `<span class="galapagos-island-label">${name}</span>`,
    iconSize: [110, 24],
    iconAnchor: [55, 12],
  });
}

export default function GalapagosExperienceMap({
  experiences,
  selectedId,
  onSelect,
}) {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markerRefs = useRef(new Map());

  useEffect(() => {
    if (!mapNodeRef.current || mapRef.current) return undefined;

    const markers = markerRefs.current;
    const isPhone = mapNodeRef.current.clientWidth < 640;
    const map = L.map(mapNodeRef.current, {
      center: [-0.7, -90.55],
      zoom: 7,
      zoomSnap: isPhone ? 1 : 0.25,
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      touchZoom: false,
      tap: false,
    });

    L.tileLayer(
      'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        maxZoom: 19,
      },
    ).addTo(map);

    ISLAND_LABELS.forEach((island) => {
      L.marker(island.coords, {
        icon: islandLabelIcon(island.name),
        interactive: false,
        keyboard: false,
        zIndexOffset: 100,
      }).addTo(map);
    });

    experiences.forEach((experience, index) => {
      const marker = L.marker(experience.coords, {
        icon: experienceIcon(index),
        keyboard: true,
        title: experience.title,
        alt: experience.title,
        zIndexOffset: 500,
      }).addTo(map);

      marker.on('click', () => onSelect(experience.id));
      markers.set(experience.id, { marker, index });
    });

    map.fitBounds(
      [
        [-1.63, -91.86],
        [0.52, -89.14],
      ],
      { padding: [22, 22] },
    );

    const resizeTimer = window.setTimeout(() => map.invalidateSize(), 0);
    mapRef.current = map;

    return () => {
      window.clearTimeout(resizeTimer);
      markers.clear();
      map.remove();
      mapRef.current = null;
    };
  }, [experiences, onSelect]);

  useEffect(() => {
    markerRefs.current.forEach(({ marker, index }, id) => {
      marker.setIcon(experienceIcon(index, id === selectedId));
    });
  }, [selectedId]);

  return (
    <div className="galapagos-map-wrap">
      <div className="galapagos-map__canvas">
        <div
          ref={mapNodeRef}
          className="galapagos-map"
          data-testid="galapagos-experience-map"
          aria-label="Map of the Galápagos Islands and honeymoon experiences"
        />
        <p className="galapagos-map__credit">
          ©{' '}
          <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
            OpenStreetMap contributors
          </a>{' '}
        </p>
      </div>
      <div className="galapagos-map__key" aria-hidden="true">
        <span><i /> Honeymoon experience</span>
      </div>
    </div>
  );
}
